import { SourceLinks } from '@/components/ui/source-links';
import { ImportanceBadge } from '@/components/ui/importance-badge';
import { DatePopover } from '@/components/ui/date-popover';
import { Badge } from '@/components/ui/badge';

interface CompositionDetailsCardProps {
  composition: HistoricalEventsByBook['composition'];
}

/**
 * Shared component for displaying composition details (when/where/how a book was written)
 * Used in both the book context page and the capability modal
 */
export function CompositionDetailsCard({ composition }: CompositionDetailsCardProps) {
  return (
    <div className="border rounded-lg p-4 space-y-3">
      {/* Title and Date */}
      <div>
        <div className="flex items-center justify-between gap-2 mb-2">
          <h4 className="text-sm font-semibold">{composition.title}</h4>
          <div className="flex items-center gap-2">
            <ImportanceBadge importance={composition.importance} />
            <DatePopover
              date={composition.compositionDate}
              alternativeDates={composition.compositionDateAlternatives}
              title="Composition Date"
            />
          </div>
        </div>
      </div>

      {/* Summary */}
      {composition.summary && (
        <div>
          <p className="text-sm font-medium text-muted-foreground mb-1">Summary</p>
          <p className="text-sm">{composition.summary}</p>
        </div>
      )}

      {/* Region and Empire */}
      <div className="flex flex-wrap gap-2">
        {composition.region && (
          <Badge variant="outline" className="text-xs">
            📍 {composition.region}
          </Badge>
        )}
        {composition.empire && (
          <Badge variant="outline" className="text-xs">
            🏛️ {composition.empire} Empire
          </Badge>
        )}
      </div>

      {/* Key Figures */}
      {composition.keyFigures && composition.keyFigures.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-muted-foreground mb-1">Key Figures</p>
          <div className="flex flex-wrap gap-1">
            {composition.keyFigures.map((figure, i) => (
              <Badge key={i} variant="secondary" className="text-xs">
                {figure}
              </Badge>
            ))}
          </div>
        </div>
      )}

      {/* Description */}
      {composition.description && (
        <div>
          <p className="text-xs font-semibold text-muted-foreground mb-1">Description</p>
          <p className="text-sm leading-relaxed whitespace-pre-line">{composition.description}</p>
        </div>
      )}

      {/* Relevance */}
      {composition.relevance && (
        <div>
          <p className="text-xs font-semibold text-muted-foreground mb-1">Why This Matters</p>
          <p className="text-sm leading-relaxed whitespace-pre-line">{composition.relevance}</p>
        </div>
      )}

      {/* Sources */}
      {composition.sources && composition.sources.length > 0 && (
        <div className="pt-2 border-t">
          <SourceLinks sources={composition.sources} />
        </div>
      )}
    </div>
  );
}

